import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { padValue } from '@celerofinancas/core-ts';
import { CdsPaginationModule } from '@celerofinancas/design-system';
import { CompanyState } from '@celerofinancas/state-management';
import { environment } from '@environments/environment';
import { CommonInputsModule, NoContentModule } from '@celerofinancas/ui-common';
import { EditContractedProductModalComponent } from './edit-contracted-product-modal/edit-contracted-product-modal.component';
import { ModalCloseEvent, ModalService } from '@celerofinancas/ui-modals';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableAction,
  DataTableConfig,
  DataTableCustomCellActionEvent,
  DataTableRow,
} from '@shared/components/data-table/data-table.interface';
import { MainTitleComponent } from '@shared/components/main-title/main-title.component';
import { NavigationBarComponent } from '@shared/components/navigation-bar/navigation-bar.component';
import { BootstrapCoordinator } from '@shared/models/bootstrap-coordinator';
import { OpenFinanceDirectoryService } from '@shared/services/open-finance-directory.service';
import { getBankLogoUrl } from '@shared/utils/open-finance-bank-map';
import {
  catchError,
  filter,
  finalize,
  Observable,
  of,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import {
  CREDIT_CARDS_TABLE_DATA_SOURCE,
  CreditCardsTableDataResult,
  CreditCardsTableDataSource,
  CreditCardsTableItem,
  CreditCardsTablePagination,
  CreditCardsTableQuery,
} from './credit-cards-data-source.token';
import {
  CREDIT_CARDS_TABLE_PAGE_SIZE,
  CreditCardsFiltersFormService,
} from './credit-cards-filters-form.service';
import { CreditCardIdentityService } from './credit-card-identity.service';

/**
 * Credit cards filters form values.
 */
interface CreditCardsFiltersFormValue {
  page: number;
  offset: number;
  search: string;
}

/**
 * CreditCardsComponent
 */
@Component({
  selector: 'caixa-webcomponent-credit-cards',
  standalone: true,
  templateUrl: './credit-cards.component.html',
  styleUrl: './credit-cards.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MainTitleComponent,
    NavigationBarComponent,
    CommonInputsModule,
    NoContentModule,
    DataTableComponent,
    CdsPaginationModule,
  ],
  providers: [CreditCardsFiltersFormService],
})
export class CreditCardsComponent extends BootstrapCoordinator implements OnInit {
  /**
   * Table loading state
   */
  public loading = signal(false);

  /**
   * Error state for table requests
   */
  public hasError = signal(false);

  /**
   * Search term rendered in search input
   */
  public searchTerm = signal('');

  /**
   * Last update date rendered in subtitle
   */
  public lastUpdate = signal(new Date());

  /**
   * Table rows rendered by DataTable component
   */
  public tableRows = signal<DataTableRow[]>([]);

  /**
   * Last contracted product selected for edit action
   */
  public selectedContractedProductName = signal('');

  /**
   * Pagination state
   */
  public pagination = signal<CreditCardsTablePagination>({
    page: 1,
    offset: CREDIT_CARDS_TABLE_PAGE_SIZE,
    totalItems: 0,
    totalPages: 0,
  });

  /**
   * Color map for bank logo column
   */
  private readonly bankLogoColorMap: Record<string, string> = {
    'Banco Bradesco S.A.': '#B91C1C',
    'Banco do Brasil S.A.': '#D97706',
    'Banco Santander (Brasil) S.A.': '#DC2626',
    'Caixa Econômica Federal': '#1D4ED8',
    'Itaú Unibanco S.A.': '#EA580C',
  };

  /**
   * Credit cards table configuration
   */
  public tableConfig: DataTableConfig = {
    sortable: false,
    emptyMessage: 'Nenhum cartão encontrado',
    showActions: true,
    actionsColumnWidth: '56px',
    rowStyle: 'plain',
    columns: [
      {
        key: 'bank',
        label: 'Banco',
        align: 'center',
        width: '72px',
        renderType: 'logo',
        renderConfig: {
          logoConfig: {
            imageUrlKey: 'institutionLogoUrl',
            colorMap: this.bankLogoColorMap,
            defaultColor: 'var(--cds-primary-600)',
          },
        },
      },
      {
        key: 'brand',
        label: 'Bandeira',
        width: '160px',
      },
      {
        key: 'contractedProduct',
        label: 'Produto contratado',
        width: '260px',
        renderType: 'custom',
        renderConfig: {
          customLayout: 'between',
          customRenderer: (value: unknown): string => this.renderContractedProductCell(value),
        },
      },
      {
        key: 'totalLimit',
        label: 'Limite total',
        align: 'right',
        width: '180px',
        renderType: 'custom',
        renderConfig: {
          customRenderer: (value: unknown, row: DataTableRow): string =>
            this.renderLimitCell(value, row),
        },
      },
      {
        key: 'usedLimit',
        label: 'Limite utilizado',
        align: 'right',
        width: '180px',
        renderType: 'currency',
        renderConfig: {
          currency: 'BRL',
          showSymbol: true,
          customClass: 'currency-dark',
        },
      },
      {
        key: 'availableLimit',
        label: 'Limite disponível',
        align: 'right',
        width: '180px',
        renderType: 'custom',
        renderConfig: {
          customRenderer: (value: unknown, row: DataTableRow): string =>
            this.renderLimitCell(value, row),
        },
      },
    ],
  };

  /**
   * Table row actions
   */
  public tableActions: DataTableAction[] = [
    {
      icon: 'cds-icon-arrow-right',
      label: 'Navegar para outra tela',
      action: (row: DataTableRow) => this.onNavigateToDetails(row),
      variant: 'solid',
      size: 'lg',
      showLabel: false,
    },
  ];

  /**
   * Filters form service
   */
  private readonly form = inject(CreditCardsFiltersFormService);

  /**
   * Router service
   */
  private readonly router = inject(Router);

  /**
   * Modal service.
   */
  private readonly modalService = inject(ModalService);

  /**
   * Data source used by the credit cards table
   */
  private readonly tableDataSource = inject<CreditCardsTableDataSource>(
    CREDIT_CARDS_TABLE_DATA_SOURCE,
  );

  /**
   * Credit card identity service used to persist the selected card context
   */
  private readonly creditCardIdentity = inject(CreditCardIdentityService);

  /**
   * Open Finance directory service for dynamic bank logos
   */
  private readonly openFinanceDirectory = inject(OpenFinanceDirectoryService);

  /**
   * Last fetched items, kept to re-map rows once the bank directory finishes loading
   */
  private lastItems: CreditCardsTableItem[] = [];

  /**
   * Current page from filters state
   */
  public get currentPage(): number {
    return this.form.filters.controls.page.value ?? this.form.initialValues.page;
  }

  /**
   * Current client identity number
   */
  private get clientIdentityNumber(): string {
    return padValue(
      this.store.selectSnapshot(CompanyState.getActiveCompany)?.identity?.number || 0,
      14,
    );
  }

  /**
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   */
  public override ngOnInit(): void {
    super.ngOnInit();
    this.watchFiltersChanges();
    this.openFinanceDirectory.load();
    this.openFinanceDirectory.loaded$
      .pipe(takeUntil(this.destroyController$))
      .subscribe(() => this.tableRows.set(this.mapRows(this.lastItems)));
  }

  /**
   * Handles search input changes.
   *
   * @param searchTerm search term
   */
  public onSearchChange(searchTerm: string): void {
    const normalizedSearchTerm = searchTerm?.trim() ?? '';

    if (
      normalizedSearchTerm === this.searchTerm() &&
      this.currentPage === this.form.initialValues.page
    ) {
      return;
    }

    this.searchTerm.set(normalizedSearchTerm);
    this.form.filters.patchValue({
      search: normalizedSearchTerm,
      page: this.form.initialValues.page,
    });
  }

  /**
   * Handles pagination page changes.
   *
   * @param page target page
   */
  public onCurrentPageChange(page: number): void {
    const safePage = Number.isFinite(page) ? Math.max(1, page) : this.form.initialValues.page;

    if (safePage === this.currentPage) {
      return;
    }

    this.form.filters.controls.page.setValue(safePage);
  }

  /**
   * Handles page size changes.
   *
   * @param offset page size
   */
  public onOffsetChange(offset: number): void {
    const safeOffset = Number.isFinite(offset)
      ? Math.max(1, offset)
      : this.form.initialValues.offset;

    this.form.filters.patchValue({
      offset: safeOffset,
      page: this.form.initialValues.page,
    });
  }

  /**
   * Handles edit action click for contracted product name.
   *
   * @param row selected row
   */
  public onEditContractedProduct(row: DataTableRow): void {
    const id = this.resolveRowId(row);

    if (!id) {
      return;
    }

    const contractedProductName = String(row.contractedProduct ?? '').trim();
    this.selectedContractedProductName.set(contractedProductName);

    const modalRef = this.modalService.open(
      EditContractedProductModalComponent,
      {
        id,
        name: contractedProductName,
        showHeader: false,
      },
      {
        modal: {
          closeStrategy: 'static',
        },
      },
    );

    modalRef.afterClosed$
      .pipe(take(1), takeUntil(this.destroyController$))
      .subscribe((event: ModalCloseEvent<unknown>) => {
        if (event.type !== 'close' || !event.data) {
          return;
        }

        this.refreshTable();
      });
  }

  /**
   * Handles custom actions triggered inside table cells.
   *
   * @param event custom cell action event
   */
  public onCustomCellAction(event: DataTableCustomCellActionEvent): void {
    if (
      event.columnKey !== 'contractedProduct' ||
      event.action !== 'open-contracted-product-modal'
    ) {
      return;
    }

    this.onEditContractedProduct(event.row);
  }

  /**
   * Handles arrow action click to navigate to another screen.
   *
   * @param row selected row
   */
  public onNavigateToDetails(row: DataTableRow): void {
    this.creditCardIdentity.set(this.rowToTableItem(row));

    void this.router.navigate(['credit-card-summary'], {
      queryParams: {
        id: String(row.id ?? ''),
      },
    });
  }

  /**
   * Subscribes to filters changes and requests table data.
   */
  private watchFiltersChanges(): void {
    this.store
      .select(CompanyState.getActiveCompany)
      .pipe(
        filter((company) => !!company),
        switchMap(() =>
          this.form.filters.valueChanges.pipe(
            startWith(this.form.initialValues),
            switchMap((formValue: Partial<CreditCardsFiltersFormValue>) =>
              this.requestList(this.getNormalizedFiltersValue(formValue)),
            ),
          ),
        ),
        takeUntil(this.destroyController$),
      )
      .subscribe((result: CreditCardsTableDataResult) => {
        this.lastItems = result.items;
        this.tableRows.set(this.mapRows(result.items));
        this.pagination.set(result.pagination);
        this.updateLastUpdate(result.lastUpdated);
      });
  }

  /**
   * Requests list using configured data source.
   *
   * @param filters filters values
   * @returns observable with normalized data
   */
  private requestList(
    filters: CreditCardsFiltersFormValue,
  ): Observable<CreditCardsTableDataResult> {
    this.loading.set(true);
    this.hasError.set(false);

    const query: CreditCardsTableQuery = {
      cnpj: this.clientIdentityNumber,
      page: filters.page,
      offset: filters.offset,
      search: filters.search,
    };

    return this.tableDataSource.list(query).pipe(
      catchError(() => {
        this.hasError.set(true);

        return of({
          items: [],
          pagination: {
            page: filters.page,
            offset: filters.offset,
            totalItems: 0,
            totalPages: 0,
          },
        });
      }),
      finalize(() => {
        this.loading.set(false);
      }),
    );
  }

  /**
   * Normalizes filters form values.
   *
   * @param value form value
   * @returns normalized filters
   */
  private getNormalizedFiltersValue(
    value: Partial<CreditCardsFiltersFormValue>,
  ): CreditCardsFiltersFormValue {
    const page = Number(value.page ?? this.form.initialValues.page);
    const offset = Number(value.offset ?? this.form.initialValues.offset);

    return {
      page: Number.isFinite(page) ? Math.max(1, page) : this.form.initialValues.page,
      offset: Number.isFinite(offset) ? Math.max(1, offset) : this.form.initialValues.offset,
      search: value.search?.trim() ?? this.searchTerm(),
    };
  }

  /**
   * Maps normalized items to DataTable rows.
   *
   * @param items normalized items
   * @returns data table rows
   */
  private mapRows(items: CreditCardsTableItem[]): DataTableRow[] {
    return items.map((item: CreditCardsTableItem) => ({
      id: item.id,
      bank: item.bank || '-',
      bankCode: item.bankCode || '',
      institutionLogoUrl: this.resolveInstitutionLogoUrl(item.institutionCnpj),
      brand: item.brand?.toUpperCase() || '-',
      name: item.name,
      contractedProduct: item.contractedProduct || '-',
      totalLimit: item.totalLimit,
      usedLimit: item.usedLimit,
      availableLimit: item.availableLimit,
      isLimitFlexible: item.isLimitFlexible ?? false,
      dueDay: item.dueDay,
    }));
  }

  /**
   * Resolves the bank logo URL from the institution CNPJ, mirroring the credit-lines
   * logo column: first the static core-ts bank map, then the Open Finance directory.
   *
   * @param institutionCnpj institution CNPJ (company_cnpj from the account payload)
   * @returns logo image URL or undefined when no logo is resolvable
   */
  private resolveInstitutionLogoUrl(institutionCnpj?: string): string | undefined {
    if (!institutionCnpj) {
      return undefined;
    }

    return (
      getBankLogoUrl(institutionCnpj, environment.staticUrl) ??
      this.openFinanceDirectory.getLogoUrl(institutionCnpj) ??
      undefined
    );
  }

  /**
   * Renders limit cells, showing a "Limite Flexível" label when the limit is flexible.
   *
   * @param value raw cell value
   * @param row data table row
   * @returns sanitized html string
   */
  private renderLimitCell(value: unknown, row: DataTableRow): string {
    if (row['isLimitFlexible'] === true) {
      return '<span class="credit-cards-limit-cell">Limite Flexível</span>';
    }

    const numericValue = typeof value === 'number' && Number.isFinite(value) ? value : null;
    const formatted =
      numericValue === null
        ? '-'
        : new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(numericValue);

    return `<span class="credit-cards-limit-cell">${this.escapeHtml(formatted)}</span>`;
  }

  /**
   * Renders contracted product cell content with edit icon.
   *
   * @param value contracted product value
   * @returns sanitized html string
   */
  private renderContractedProductCell(value: unknown): string {
    const contractedProduct =
      typeof value === 'string' && !!value.trim() ? value.trim() : '-';
    const escapedContractedProduct = this.escapeHtml(contractedProduct);

    return `
      <span>${escapedContractedProduct}</span>
      <span
        role="button"
        tabindex="0"
        aria-label="Editar produto contratado"
        class="custom-cell-action-button custom-cell-action--open-contracted-product-modal"
      >
        <i class="cds-icon-edit custom-cell-action-icon"></i>
      </span>
    `;
  }

  /**
   * Escapes html-sensitive characters from custom cell content.
   *
   * @param value raw text value
   * @returns escaped text
   */
  private escapeHtml(value: string): string {
    const htmlEscapes: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      '\'': '&#39;',
    };

    return value.replace(/[&<>"']/g, (character: string) => htmlEscapes[character] || character);
  }

  /**
   * Updates last update value when payload includes a valid date.
   *
   * @param lastUpdated last update value from payload
   */
  private updateLastUpdate(lastUpdated?: string): void {
    if (!lastUpdated) {
      return;
    }

    const parsedDate = new Date(lastUpdated);

    if (!Number.isNaN(parsedDate.getTime())) {
      this.lastUpdate.set(parsedDate);
    }
  }

  /**
   * Resolves row id used for update request.
   *
   * @param row selected row
   * @returns row id or empty string
   */
  private resolveRowId(row: DataTableRow): string {
    const { id } = row;

    if (typeof id === 'number' && Number.isFinite(id)) {
      return String(id);
    }

    if (typeof id === 'string') {
      return id.trim();
    }

    return '';
  }

  /**
   * Maps a DataTableRow to a CreditCardsTableItem for identity storage.
   *
   * @param row selected row
   * @returns credit cards table item
   */
  private rowToTableItem(row: DataTableRow): CreditCardsTableItem {
    return {
      id: String(row.id ?? ''),
      bank: String(row.bank ?? ''),
      bankCode: String(row.bankCode ?? ''),
      brand: String(row.brand ?? ''),
      name: row.name ? String(row.name) : undefined,
      contractedProduct: String(row.contractedProduct ?? ''),
      totalLimit: typeof row.totalLimit === 'number' ? row.totalLimit : null,
      usedLimit: typeof row.usedLimit === 'number' ? row.usedLimit : null,
      availableLimit: typeof row.availableLimit === 'number' ? row.availableLimit : null,
      isLimitFlexible: row['isLimitFlexible'] === true,
      dueDay: typeof row.dueDay === 'number' ? row.dueDay : undefined,
    };
  }

  /**
   * Refreshes table after successful modal update.
   */
  private refreshTable(): void {
    this.form.filters.patchValue({
      page: this.currentPage,
      offset: this.pagination().offset,
      search: this.searchTerm(),
    });
  }
}
